/**
 * Sending a survey back, with the reason attached.
 *
 * The reason is required in the BFF and read by whoever submitted it, right inside the builder —
 * so this is a dialog with a field rather than a plain confirmation: "rejeitada" with no
 * explanation is a round trip nobody can act on.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTModal, UTTextArea } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  padding-bottom: var(--space-s);
`;

const hintStyle = css`
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

export interface SurveyRejectDialogProps {
  isWorking: boolean;
  onReject: (reason: string) => void;
  onClose: () => void;
}

export const SurveyRejectDialog = ({
  isWorking,
  onReject,
  onClose,
}: SurveyRejectDialogProps): JSX.Element => {
  const [reason, setReason] = useState('');

  return (
    <UTModal
      open
      position='center'
      maxWidth='480px'
      hasButtonClose
      modalContentId='survey-reject-dialog'
      closeButtonId='survey-reject-dialog-close'
      title='Rejeitar pesquisa'
      content={
        <div className={contentStyle}>
          <p className={hintStyle}>
            Diga o que precisa mudar. Quem enviou lê este texto no construtor, e é com ele que a
            próxima tentativa chega diferente.
          </p>
          <UTTextArea
            id='survey-reject-reason'
            textAreaSize='small'
            placeholder='O enunciado da pergunta 3 pergunta duas coisas de uma vez.'
            value={reason}
            onChange={event => {
              setReason(event.target.value);
            }}
          />
        </div>
      }
      customButtons={[
        { text: 'Cancelar', variant: 'secondary', onClick: onClose },
        {
          text: 'Rejeitar',
          state: 'danger',
          loading: isWorking,
          disabled: reason.trim() === '',
          onClick: () => {
            onReject(reason.trim());
          },
        },
      ]}
      onClose={onClose}
    />
  );
};
